// OP-3605 — Regras puras da tela de remessa, espelho de `domain/vinculo.ts`
// (validarColeta, restanteEmCampo, conferência peça a peça). Gates visuais;
// quem recusa de verdade é o servidor.
import { CicloVida, DecisaoConferencia, RemessaItem, StatusRemessa } from '../../../../types/estoque'
import { faltaMotivo } from '../formato'

export const STATUS_REMESSA_UI: Record<StatusRemessa, { bg: string; color: string; label: string }> = {
  EM_SEPARACAO: { bg: '#E0E7FF', color: '#3730A3', label: 'Em separação' },
  DESPACHADA: { bg: '#DBEAFE', color: '#1E3A8A', label: 'Despachada' },
  ENTREGUE: { bg: '#D1FAE5', color: '#065F46', label: 'Entregue (em campo)' },
  EM_RETORNO: { bg: '#FED7AA', color: '#9A3412', label: 'Em retorno' },
  EM_CONFERENCIA: { bg: '#FEF3C7', color: '#92400E', label: 'Em conferência' },
  ENCERRADA: { bg: '#F3F4F6', color: '#374151', label: 'Encerrada' },
  CANCELADA: { bg: '#F3F4F6', color: '#374151', label: 'Cancelada' },
}

export type AcaoRemessa = 'ENTREGA' | 'COLETA' | 'RECEBIMENTO' | 'CONFERENCIA' | null

/** A única ação que cada status admite (espelho dos `Error409` do service). */
export function acaoDoStatus(status: StatusRemessa): AcaoRemessa {
  switch (status) {
    case 'DESPACHADA': return 'ENTREGA'
    case 'ENTREGUE': return 'COLETA'
    case 'EM_RETORNO': return 'RECEBIMENTO'
    case 'EM_CONFERENCIA': return 'CONFERENCIA'
    default: return null
  }
}

/** OP-3619 — o nome da ação do status, igual na lista e na barra fixa. */
export const ROTULO_ACAO: Record<Exclude<AcaoRemessa, null>, string> = {
  ENTREGA: 'Confirmar chegada',
  COLETA: 'Registrar coleta',
  RECEBIMENTO: 'Confirmar recebimento',
  CONFERENCIA: 'Conferir peça a peça',
}

/** O que ainda está com o detentor, por linha. */
export const emCampo = (l: Pick<RemessaItem, 'quantidade' | 'quantidadeRetornada'>): number =>
  Math.max(0, Number(l.quantidade) - Number(l.quantidadeRetornada))

/** Espelho de `pecaEhColetavel` do domínio. */
export const pecaEhColetavel = (c: CicloVida | null | undefined): boolean =>
  c !== 'EXTRAVIADO' && c !== 'BAIXADO'

/**
 * A linha pode voltar numa coleta?
 *
 * Duas exclusões, as MESMAS que o service aplica antes de validar: o
 * consumível saiu do patrimônio na ENTREGA e não está em campo para ser
 * coletado; a peça declarada extraviada (ou baixada) fica com
 * `quantidade_retornada` zero para sempre e reapareceria em toda viagem — o
 * caminho dela é a fila de baixa.
 *
 * Sem isto, "marcar todas" numa remessa mista mandava ids que o servidor
 * recusa, e a sobra de consumo — que não existe — exigia nova previsão.
 */
export const ehColetavel = (l: Pick<RemessaItem, 'unidadeId' | 'cicloVida'>): boolean =>
  l.unidadeId != null && pecaEhColetavel(l.cicloVida)

/** As linhas que uma coleta pode trazer, já sem consumo nem peça perdida. */
export const linhasColetaveis = (itens: RemessaItem[]): RemessaItem[] =>
  itens.filter((l) => ehColetavel(l) && emCampo(l) > 0)

/**
 * As linhas que ESTA viagem trouxe — as únicas que a conferência decide.
 *
 * Espelho do `WHERE unidade_id IS NOT NULL AND em_viagem > 0` do service:
 * exigir decisão para a peça que ficou com o produtor (ou para o consumo)
 * fazia a tela mandar linhas que o servidor recusa, e a conferência inteira
 * falhava — justamente no fluxo mais comum, o da coleta parcial.
 */
export const linhasDaViagem = (itens: RemessaItem[]): RemessaItem[] =>
  itens.filter((l) => l.unidadeId != null && Number(l.emViagem) > 0)

/** Consumível da remessa: fato da entrega, não linha de coleta nem de conferência. */
export const linhasDeConsumo = (itens: RemessaItem[]): RemessaItem[] =>
  itens.filter((l) => l.unidadeId == null)

/** Peça que saiu e não volta mais: extraviada ou baixada, ainda "não retornada". */
export const linhasPerdidas = (itens: RemessaItem[]): RemessaItem[] =>
  itens.filter((l) => l.unidadeId != null && !pecaEhColetavel(l.cicloVida) && emCampo(l) > 0)

export interface ColetaForm { quantidades: Record<number, number>; novaPrevisao: string }

/**
 * Sobra com o detentor depois desta viagem (espelho de `restanteEmCampo`).
 * Recebe só linhas coletáveis: é essa a conta que decide se há nova previsão.
 */
export function restanteEmCampo(itens: RemessaItem[], q: Record<number, number>): number {
  return itens.reduce((s, l) => s + Math.max(0, emCampo(l) - (q[l.id] || 0)), 0)
}

/**
 * Primeiro impedimento da coleta, ou null (espelho de `validarColeta`).
 * `itens` são as linhas coletáveis (`linhasColetaveis`), nunca a remessa toda.
 */
export function impedimentoColeta(itens: RemessaItem[], f: ColetaForm, hoje: string): string | null {
  let total = 0
  for (const l of itens) {
    const v = f.quantidades[l.id] || 0
    if (!Number.isInteger(v) || v < 0) return `Quantidade inválida em ${l.itemNome}.`
    if (v > emCampo(l)) return `${l.itemNome}: só há ${emCampo(l)} em campo.`
    total += v
  }
  if (total <= 0) return 'Marque o que está voltando nesta viagem.'
  if (restanteEmCampo(itens, f.quantidades) > 0) {
    const d = f.novaPrevisao.trim()
    if (!d) return 'Fica equipamento com o detentor: informe a nova previsão de retorno.'
    if (d < hoje) return 'A nova previsão não pode ficar no passado.'
  }
  return null
}

export interface LinhaConferencia { decisao: DecisaoConferencia | ''; quantidadeRetornada: string; observacao: string }

/**
 * Conferência decide linha a linha: toda linha da VIAGEM precisa de decisão;
 * NAO_RETORNOU zera; serializado é 1 ou 0; nunca acima do que saiu. O ramo de
 * quantidade fica por precaução — a viagem só traz peça serializada.
 *
 * `itens` são as linhas da viagem (`linhasDaViagem`), nunca a remessa toda.
 */
export function impedimentoConferencia(itens: RemessaItem[], f: Record<number, LinhaConferencia>): string | null {
  if (!itens.length) return 'Nada desta viagem para conferir.'
  for (const l of itens) {
    const x = f[l.id]
    if (!x || !x.decisao) return `Decida o que aconteceu com ${l.serial ?? l.itemNome}.`
    if (l.controle === 'QUANTIDADE' && x.decisao !== 'NAO_RETORNOU') {
      const q = x.quantidadeRetornada.trim()
      if (q === '') return `${l.itemNome}: informe a quantidade que voltou (zero é zero, em branco não).`
      const n = Number(q)
      if (!Number.isInteger(n) || n < 0 || n > Number(l.quantidade)) return `${l.itemNome}: quantidade entre 0 e ${l.quantidade}.`
    }
  }
  return null
}

export function montarConferencia(itens: RemessaItem[], f: Record<number, LinhaConferencia>) {
  return itens.map((l) => {
    const x = f[l.id]
    const base = { remessaItemId: l.id, decisao: x.decisao as DecisaoConferencia, observacao: x.observacao.trim() || null }
    if (l.controle === 'SERIAL') return { ...base, quantidadeRetornada: x.decisao === 'NAO_RETORNOU' ? 0 : 1 }
    return { ...base, quantidadeRetornada: x.decisao === 'NAO_RETORNOU' ? 0 : Number(x.quantidadeRetornada) }
  })
}

/** A frase do desfecho decide pelos ACUMULADOS da remessa, não pela viagem (R7-03). */
export function fraseDesfecho(pecas: number, retornadas: number, perdidas: number): string {
  if (pecas === 0 && perdidas === 0) return `Tudo voltou: ${retornadas} peça${retornadas === 1 ? '' : 's'} conferida${retornadas === 1 ? '' : 's'}. A remessa fecha.`
  if (pecas === 0 && perdidas > 0) return `${retornadas} ${retornadas === 1 ? 'voltou' : 'voltaram'} e ${perdidas} não ${perdidas === 1 ? 'retornou' : 'retornaram'} (extravio registrado). A remessa fecha.`
  return `${retornadas} conferida${retornadas === 1 ? '' : 's'}, ${pecas} ainda em campo${perdidas ? `, ${perdidas} extraviada${perdidas === 1 ? '' : 's'}` : ''}. A remessa continua aberta.`
}

export const podeCancelarRemessa = (status: StatusRemessa, solicitacaoId?: number | null): boolean =>
  status === 'DESPACHADA' && !!solicitacaoId

export const impedimentoCancelarRemessa = (motivo: string): string | null =>
  faltaMotivo(motivo, 'Escreva o motivo do cancelamento: ele fica na remessa e no histórico do pedido.')
